import {
  ErrorResponse,
  RawResponse,
  ValidatedResponse,
  ValidationError,
} from "@interfaces/response"
import { StrapiResponse } from "@interfaces/strapi"
import ky from "ky"
import { HttpMethod } from "ky/distribution/types/options"
import { parseCookies } from "nookies"

export type Prefix = "STRAPI" | "BASIC"

export default class KyMethod {
  private readonly strapiInstance: typeof ky
  private readonly instance: typeof ky

  constructor() {
    this.instance = ky.extend({
      prefixUrl: process.env.API_ENDPOINT || process.env.HARISENIN_API_ENDPOINT,
    })
    this.strapiInstance = ky.extend({
      prefixUrl: process.env.STRAPI_ENDPOINT || process.env.HARISENIN_STRAPI_ENDPOINT,
    })
  }

  _getPrefix(type: Prefix) {
    if (type === "STRAPI") {
      return process.env.STRAPI_ENDPOINT || process.env.HARISENIN_API_ENDPOINT_STRAPI
    } else {
      return process.env.API_ENDPOINT || process.env.HARISENIN_API_ENDPOINT
    }
  }

  async _tokenChecker() {
    const parsedCookies = parseCookies()
    const token = parsedCookies.HSTOKENID

    if (!token) {
      return null
    }

    try {
      const res = await this.instance.post("/api/verify-token", { json: token })
      console.log(res)
    } catch (error) {
      return null
    }
  }

  protected async _getResponse<T>(res: Response): Promise<ValidatedResponse<T>> {
    if (!res.ok) {
      return {
        error: "Terjadi kesalahan. Silahkan coba lagi",
        error_code: "SERVER_ERROR",
      }
    }

    const response = (await res.json()) as RawResponse<T>

    if (response.error || response.error_code) {
      return {
        error: response.message ?? "Terjadi kesalahan. Silahkan coba lagi",
        error_code: response.error_code ?? "SERVER_ERROR",
        result: response?.data,
      }
    }

    return {
      error: "",
      error_code: "",
      result: response?.result || response?.data,
    }
  }

  protected _swaggerResponseSpreader<T>(data: any) {
    const strapiResponse = data as StrapiResponse<T>

    if (!data) {
      return null
    }

    const response: T = {
      id: strapiResponse.id,
      ...strapiResponse.attributes,
    }

    return response
  }

  protected _swaggerDataSpreader<T>(tags: any) {
    const strapiResponse = tags.data as StrapiResponse<T>[]

    const responses: T[] = strapiResponse.map((d) => {
      return {
        id: d.id,
        ...d.attributes,
      }
    })

    return responses
  }

  protected async _getErrorObject(error: any): Promise<ErrorResponse> {
    const errResponse = error.response

    if (!errResponse) {
      return {
        message: "Terjadi kesalahan",
        error_code: "SERVER_ERROR",
      }
    }

    const errorData = await errResponse.json()

    if (errorData.error_code) {
      return {
        message: errorData.message,
        error_code: errorData.error_code,
      }
    } else {
      const errors = errorData.errors as ValidationError

      return {
        message: errors.message,
        error_code: errors.validation,
      }
    }
  }

  /**
   * Get method for STRAPI
   */
  protected async _getStrapiData(
    url: string,
    options?: {
      query?: any
    }
  ) {
    return this.strapiInstance.get("api" + url, {
      searchParams: {
        populate: "*",
        ...(options?.query && { ...options.query }),
      },
    })
  }

  /**
   * Get method for public API
   */
  protected async _getPublicData(
    url: string,
    options: {
      version?: "v2" | "v3"
      query?: any
    } = { version: "v2", query: {} }
  ) {
    const version = options.version ?? "v2"
    return this.instance.get(version + url, {
      searchParams: options.query ?? {},
    })
  }

  /**
   * GET method for protected API
   */
  protected async _getProtectedData(
    url: string,
    options?: {
      version?: "v2" | "v3"
      query?: any
    }
  ) {
    const token = this._tokenChecker()

    if (token) {
      return this.instance.get((options?.version ?? "v2") + url, {
        ...(options?.query && { searchParams: options.query }),
        headers: {
          Authorization: "Bearer " + token,
        },
      })
    } else {
      throw {
        statusCode: 401,
        message: "Sesi login telah habis. Silahkan login ulang",
      }
    }
  }

  /**
   * GET method for protected STRAPI
   */
  protected async _getStrapiProtectedData(
    url: string,
    options?: {
      query?: any
    }
  ) {
    const token = process.env.STRAPI_TOKEN || process.env.HARISENIN_STRAPI_TOKEN

    if (token) {
      return this.strapiInstance.get("api" + url, {
        ...(options?.query && { searchParams: options.query }),
        headers: {
          Authorization: "Bearer " + token,
        },
      })
    } else {
      throw {
        statusCode: 401,
        message: "Invalid token",
      }
    }
  }

  /**
   * CRUD method for public API
   */
  protected async _crudPublicMethod(
    url: string,
    body: any = {},
    options?: {
      method: HttpMethod
      version: "v2" | "v3"
      bodyType?: "form-data" | "json"
    }
  ) {
    const type = options?.bodyType ?? "json"

    return this.instance((options?.version ?? "v2") + url, {
      method: options?.method ?? "post",
      ...(type === "form-data" && { body }),
      ...(type === "json" && { json: body }),
    })
  }

  /**
   * CRUD method for STRAPI
   */
  protected async _crudStrapiMethod(
    url: string,
    options?: {
      body?: any
      query?: any
      method?: HttpMethod
      bodyType?: "form-data" | "json"
    }
  ) {
    const type = options?.bodyType ?? "json"
    return this.strapiInstance("api" + url, {
      ...(options?.query && { searchParams: options.query }),
      ...(type === "form-data" && { body: options?.body }),
      ...(type === "json" && { json: options?.body ?? {} }),
      method: options?.method ?? "post",
    })
  }

  /**
   * CRUD method for STRAPI
   */
  protected async _protectedCrudStrapiMethod(
    url: string,
    options?: {
      body?: any
      query?: any
      method?: HttpMethod
      bodyType?: "form-data" | "json"
    }
  ) {
    const token = process.env.STRAPI_TOKEN || process.env.HARISENIN_STRAPI_TOKEN

    const type = options?.bodyType ?? "json"
    return this.strapiInstance("api" + url, {
      ...(options?.query && { searchParams: options.query }),
      ...(type === "form-data" && { body: options?.body }),
      ...(type === "json" && { json: options?.body ?? {} }),
      method: options?.method ?? "post",
      headers: {
        Authorization: "Bearer " + token,
      },
    })
  }

  /**
   * CRUD method for protected API
   */
  protected async _protectedCrudMethod(
    url: string,
    options?: {
      body?: any
      version?: "v2" | "v3"
      query?: any
      method?: HttpMethod
      bodyType?: "form-data" | "json"
    }
  ) {
    const type = options?.bodyType ?? "json"
    const token = this._tokenChecker()

    if (token) {
      return this.instance((options?.version ?? "v2") + url, {
        ...(options?.query && { searchParams: options.query }),
        headers: {
          Authorization: "Bearer " + token,
        },
        ...(type === "form-data" && { body: options?.body }),
        ...(type === "json" && { json: options?.body ?? {} }),
        method: options?.method ?? "post",
      })
    } else {
      throw {
        statusCode: 401,
        message: "Sesi login telah habis. Silahkan login ulang",
      }
    }
  }
}
